import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useAppStore } from '../../stores/appStore';
import { notesApi } from '../../lib/api';

const COLORS = ['#FEF08A', '#BBF7D0', '#BFDBFE', '#FBCFE8', '#DDD6FE', '#FED7AA'];

/**
 * Sticky notes that grow/shrink to fit their text (not a fixed box).
 */
export function StickyNotesWidget() {
  const stickies = useAppStore((s) => s.stickies);
  const user = useAppStore((s) => s.user);
  const refresh = useAppStore((s) => s.refreshWidgetsData);
  const [draft, setDraft] = useState('');
  const [color, setColor] = useState(COLORS[0]);

  async function add() {
    if (!draft.trim() || !user) return;
    await notesApi.createSticky({ content: draft.trim(), color });
    setDraft('');
    await refresh();
  }

  async function update(id: string, content: string) {
    if (!user) return;
    await notesApi.updateSticky(id, { content });
    await refresh();
  }

  async function remove(id: string) {
    if (!user) return;
    if (!confirm('Delete this sticky note?')) return;
    try {
      await notesApi.deleteSticky(id);
      await refresh();
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Could not delete note');
    }
  }

  return (
    <div className="flex h-full flex-col">
      <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">
        Sticky notes
      </h3>
      <div className="flex flex-1 flex-wrap content-start items-start gap-2 overflow-auto">
        {stickies.map((s) => (
          <AutoSizeSticky
            key={s.id}
            id={s.id}
            content={s.content}
            color={s.color}
            canEdit={Boolean(user)}
            onSave={(content) => void update(s.id, content)}
            onRemove={() => void remove(s.id)}
          />
        ))}
      </div>
      {user && (
        <div className="mt-2 flex gap-1">
          <input
            className="min-w-0 flex-1 rounded-lg border border-slate-600 bg-slate-800 px-2 py-1.5 text-sm outline-none focus:border-violet-500"
            placeholder="New note…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void add()}
          />
          <div className="flex items-center gap-0.5">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`h-5 w-5 rounded-full border-2 ${
                  color === c ? 'border-white' : 'border-transparent'
                }`}
                style={{ background: c }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => void add()}
            className="rounded-lg bg-violet-600 px-2 py-1 text-sm font-bold text-white"
          >
            +
          </button>
        </div>
      )}
    </div>
  );
}

function AutoSizeSticky({
  id,
  content,
  color,
  canEdit,
  onSave,
  onRemove,
}: {
  id: string;
  content: string;
  color: string;
  canEdit: boolean;
  onSave: (content: string) => void;
  onRemove: () => void;
}) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(content);

  useEffect(() => {
    setValue(content);
  }, [content, id]);

  const fit = useCallback(() => {
    const el = taRef.current;
    if (!el) return;
    // Height → content
    el.style.height = '0px';
    el.style.height = `${Math.max(el.scrollHeight, 28)}px`;
    // Width → roughly based on longest line, clamped
    const lines = el.value.split('\n');
    const longest = lines.reduce((m, l) => Math.max(m, l.length), 0);
    // ~7.5px per char at text-sm, pad for padding/delete btn
    const w = Math.min(280, Math.max(88, longest * 7.8 + 28));
    el.style.width = `${w}px`;
  }, []);

  useEffect(() => {
    fit();
  }, [value, fit]);

  return (
    <div
      className="group relative inline-block max-w-[90%] rounded-md p-2 shadow-md rotate-[-0.5deg] transition hover:rotate-0 hover:shadow-lg"
      style={{ background: color }}
    >
      <textarea
        ref={taRef}
        className="block max-w-full resize-none overflow-hidden border-0 bg-transparent p-0 text-sm leading-snug text-slate-900 outline-none"
        style={
          {
            fieldSizing: 'content',
            minWidth: '4.5rem',
            minHeight: '1.5rem',
          } as CSSProperties
        }
        value={value}
        readOnly={!canEdit}
        rows={1}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        onInput={fit}
        onBlur={() => {
          if (value !== content) onSave(value);
        }}
        spellCheck
      />
      {canEdit && (
        <button
          type="button"
          className="absolute -right-1.5 -top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-slate-900/70 text-sm font-bold text-white shadow opacity-90 transition hover:bg-red-600 group-hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          title="Delete sticky"
        >
          ×
        </button>
      )}
    </div>
  );
}
